import React from "react";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { alpha } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import api from "/imports/api/client";
import { useWidget } from "/imports/state/WidgetContext";

const MAX_EDGE_PX = 512;
const JPEG_QUALITY = 0.85;
const MAX_SOURCE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Re-encode to an avatar-sized image in the browser so a photo taken straight
// off a phone doesn't have to travel at full resolution. PNG is kept so a photo
// with transparency doesn't gain a black background.
function toResizedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const scale = Math.min(1, MAX_EDGE_PX / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);

      const isPng = file.type === "image/png";
      resolve(canvas.toDataURL(isPng ? "image/png" : "image/jpeg", JPEG_QUALITY));
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("That file could not be read as an image."));
    };

    img.src = objectUrl;
  });
}

// Warm the CDN copy before the local preview is dropped, so the photo never
// blinks back to initials between "saved" and "downloaded".
function preload(url) {
  return new Promise((resolve) => {
    if (!url) return resolve();
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = url;
  });
}

function AvatarUpload({ imgUrl, initials, size = 5, onUpdated }) {
  const { slug } = useWidget();
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const dimension = `${size}rem`;
  const shownUrl = preview || imgUrl;

  const pickFile = () => {
    setAnchorEl(null);
    inputRef.current?.click();
  };

  const handleClick = (event) => {
    if (busy) return;
    if (imgUrl) setAnchorEl(event.currentTarget);
    else pickFile();
  };

  const handleChange = async (event) => {
    const file = event.target.files?.[0];
    // Clearing the input lets the same file be picked again after a failure.
    event.target.value = "";
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      enqueueSnackbar("Please choose a JPEG, PNG or WebP image.", { variant: "error" });
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      enqueueSnackbar(`That image is too large (${Math.round(file.size / 1024 / 1024)} MB). Please use one under 10 MB.`, { variant: "error" });
      return;
    }

    setBusy(true);
    try {
      // Shown under the spinner straight away, so the wait is spent looking at
      // the photo being saved rather than at the old one.
      const dataUrl = await toResizedDataUrl(file);
      setPreview(dataUrl);

      const result = await api.uploadAvatar(slug, dataUrl);
      await onUpdated?.(result);
      await preload(result?.imgUrl);

      setPreview(null);
      enqueueSnackbar("Photo updated", { variant: "success" });
    } catch (err) {
      setPreview(null);
      enqueueSnackbar(err.message || "Your photo could not be saved. Please try again.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setAnchorEl(null);
    setBusy(true);
    try {
      const result = await api.removeAvatar(slug);
      await onUpdated?.(result);
      setPreview(null);
      enqueueSnackbar("Photo removed", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.message || "Your photo could not be removed. Please try again.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Visually hidden rather than `hidden`: iOS Safari refuses a programmatic
          click on a display:none input, which would silently do nothing on tap. */}
      <Box
        component="input"
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleChange}
        sx={{ position: "absolute", width: "1px", height: "1px", opacity: 0, pointerEvents: "none", clip: "rect(0 0 0 0)" }}
      />

      <Tooltip title={busy ? "Saving your photo…" : imgUrl ? "Change your photo" : "Add your photo"}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <Box
              sx={{
                width: "1.5rem",
                height: "1.5rem",
                borderRadius: "9999px",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid",
                borderColor: "background.paper"
              }}
            >
              <PhotoCameraIcon sx={{ fontSize: "0.875rem" }} />
            </Box>
          }
          role="button"
          tabIndex={0}
          aria-label={imgUrl ? "Change your photo" : "Add your photo"}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick(e);
            }
          }}
          sx={{ cursor: busy ? "default" : "pointer", flexShrink: 0, borderRadius: "9999px" }}
        >
          <Box component="span" sx={{ position: "relative", display: "inline-flex", borderRadius: "9999px" }}>
            <Avatar
              src={shownUrl || undefined}
              sx={{
                width: dimension,
                height: dimension,
                bgcolor: "primary.main",
                fontSize: `${size / 2.5}rem`,
                fontWeight: 600,
                transition: "filter 120ms ease",
                "&:hover": { filter: "brightness(0.92)" }
              }}
            >
              {initials}
            </Avatar>

            {busy && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.45)
                }}
              >
                <CircularProgress size="1.25rem" sx={{ color: "common.white" }} />
              </Box>
            )}
          </Box>
        </Badge>
      </Tooltip>

      {/* Anchored below the avatar so it doesn't sit on top of the greeting,
          which is what happens on a narrow screen with the default origins. */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuItem dense onClick={pickFile}>
          <ListItemIcon sx={{ minWidth: "2rem" }}>
            <PhotoCameraIcon sx={{ fontSize: "1.125rem" }} />
          </ListItemIcon>
          <ListItemText primary="Change photo" primaryTypographyProps={{ fontSize: "0.875rem" }} />
        </MenuItem>
        <MenuItem dense onClick={handleRemove}>
          <ListItemIcon sx={{ minWidth: "2rem" }}>
            <DeleteOutlineIcon sx={{ fontSize: "1.125rem" }} />
          </ListItemIcon>
          <ListItemText primary="Remove photo" primaryTypographyProps={{ fontSize: "0.875rem" }} />
        </MenuItem>
      </Menu>
    </>
  );
}

export default AvatarUpload;
